import React, { useState } from 'react';
import styled from 'styled-components';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Typography } from '@mui/material';
import { useSubmitInterviewDetail } from './graphql';
import {
  INTERVIEW_DETAIL_MODULES,
  InterviewDetailSidebar,
  INTERVIEW_LIST_PAGE_TEXT,
} from './components';
import { useGetNominationInformation } from '../Assessment/graphql';
import {
  PAGE_TEXT,
  Evaluation,
  handleCheckFinishEvaluationQuestions,
  EVALUATION_SCENE,
} from '../Nomination/components';
import { ApplicantParticulars } from '../Assessment/DRCAssessment/components/';
import {
  Alert,
  type AlertDetails,
  Dialog,
  Loading,
  type DialogDetails,
} from '../../components';
import { APPLICATION_STATUS_TYPE, SimplifyRoles } from '../../utils';

export const InterviewDetailPage: React.FunctionComponent = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { applicationId = '' } = useParams();
  const navigate = useNavigate();

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDetails, setDialogDetails] = useState<DialogDetails>({
    title: '',
    content: '',
  });

  const {
    scholarshipNominationMetadata,
    scholarshipNomination,
    isScholarshipInterviewFinished,
    status,
    loading: pageLoading,
    error: pageError,
    refetch,
  } = useGetNominationInformation({
    application: { _id: { eq: applicationId } },
  });

  if (
    !pageLoading &&
    status !== APPLICATION_STATUS_TYPE.UNDER_INTERVIEW_PANEL
  ) {
    navigate(`/internal/monitor/${applicationId}`);
  }

  const submitInterviewDetail = useSubmitInterviewDetail();

  const interviewModulesMap: Partial<
    Record<
      INTERVIEW_DETAIL_MODULES,
      { component: React.ReactNode; moduleTitle: string }
    >
  > = {
    [INTERVIEW_DETAIL_MODULES.APPLICANT_PARTICULARS]: {
      component: <ApplicantParticulars showGoDetail />,
      moduleTitle: INTERVIEW_LIST_PAGE_TEXT.APPLICANT_PARTICULARS,
    },
    [INTERVIEW_DETAIL_MODULES.INTERVIEW_DETAIL]: {
      component: (
        <Evaluation
          scene={EVALUATION_SCENE.INTERVIEW}
          applicationId={applicationId}
          scholarshipNomination={scholarshipNomination}
          refetchEvaluation={refetch}
        />
      ),
      moduleTitle: INTERVIEW_LIST_PAGE_TEXT.INTERVIEW_REPORT,
    },
  };
  // am means Activated Module
  let activatedModuleId = searchParams.get('am') as INTERVIEW_DETAIL_MODULES;
  if (!Object.values(INTERVIEW_DETAIL_MODULES).includes(activatedModuleId)) {
    activatedModuleId = INTERVIEW_DETAIL_MODULES.INTERVIEW_DETAIL;
  }

  const activatedModule:
    | { component: React.ReactNode; moduleTitle: string }
    | undefined = interviewModulesMap[activatedModuleId];

  const moduleTitle = activatedModule?.moduleTitle ?? activatedModuleId;
  const moduleComponent = activatedModule?.component ?? activatedModuleId;

  const isInterviewPanelAgree =
    scholarshipNomination.interviewPanel.panelAgreement;

  const validationChecks = [
    {
      // all questions for interview panel and all panel info has been completed
      //  Panel Agreement Box has been checked
      validate:
        handleCheckFinishEvaluationQuestions(
          scholarshipNominationMetadata,
          scholarshipNomination.questions,
          SimplifyRoles.INTERVIEW_PANEL,
        ) && isInterviewPanelAgree,
      message:
        INTERVIEW_LIST_PAGE_TEXT.ALERT
          .PLEASE_COMPLETE_ALL_QUESTIONS_IN_INTERVIEW_REPORT,
      modules: INTERVIEW_DETAIL_MODULES.INTERVIEW_DETAIL,
    },
  ];

  const handleConfirmSubmitInterviewDetail = async (): Promise<void> => {
    setDialogOpen(false);
    // submit the nomination will change the application status to UNDER_DORPGS
    try {
      await submitInterviewDetail({
        variables: {
          applicationId,
        },
      });

      setAlertDetails({
        alertContent:
          INTERVIEW_LIST_PAGE_TEXT.ALERT.SUBMIT_INTERVIEW_REPORT_SUCCESSFULLY,
        alertType: 'success',
      });
      setOpenAlert(true);
      navigate('/internal/interview-homepage');
    } catch (e) {
      setAlertDetails({
        alertContent: 'Error Occurs.' + (e as Error)?.message,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  const handleCloseDialog = (): void => {
    setDialogOpen(false);
  };

  const handleSubmitInterviewReport = async (): Promise<void> => {
    for (const check of validationChecks) {
      if (!check.validate) {
        setAlertDetails({
          alertContent: check.message,
          alertType: 'warning',
        });
        setOpenAlert(true);
        navigate(`${location.pathname}?am=${check.modules}`);
        return;
      }
    }
    // pop up a dialog to confirm the submission
    setDialogDetails({
      title:
        INTERVIEW_LIST_PAGE_TEXT.SUBMIT_CONFIRM
          .ARE_YOU_SURE_YOU_WANT_TO_SUBMIT_THE_INTERVIEW_REPORT,
      content:
        INTERVIEW_LIST_PAGE_TEXT.SUBMIT_CONFIRM
          .ONCE_SUBMITTED_THE_INTERVIEW_REPORT_WILL_BE_REVIEWED_BY_DORPGS_YOU_CANNOT_REVISE_THIS_INTERVIEW_REPORT,
      confirmAction: INTERVIEW_LIST_PAGE_TEXT.SUBMIT,
      cancelAction: INTERVIEW_LIST_PAGE_TEXT.CANCEL,
      handleDialogConfirmAction: handleConfirmSubmitInterviewDetail,
    });
    setDialogOpen(true);
  };

  if (pageError)
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: 'Error Occurs.' + (pageError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  return pageLoading ? (
    <Loading />
  ) : (
    <>
      <Dialog
        openDialog={dialogOpen}
        width={'700px'}
        dialogTitle={dialogDetails.title}
        dialogAction={dialogDetails.confirmAction}
        cancelAction={dialogDetails.cancelAction ?? undefined}
        onDialogAction={dialogDetails.handleDialogConfirmAction}
        onDialogClose={handleCloseDialog}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {dialogDetails.content}
        </Typography>
      </Dialog>
      <PageContainer>
        <div className="sidebar-area">
          <InterviewDetailSidebar
            onSubmitNomination={handleSubmitInterviewReport}
            enableSubmit={!isScholarshipInterviewFinished}
          />
        </div>
        <Container>
          <h1>
            {PAGE_TEXT.INTERVIEW_PANEL} - {moduleTitle}
          </h1>
          <div className="form-area">{moduleComponent}</div>
        </Container>
      </PageContainer>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
    </>
  );
};

const PageContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  flex: 1;
  width: 100%;

  .sidebar-area {
    width: 18.5rem;
    border-right: 0.1rem solid #eee;
    padding-right: 0.1rem;
  }

  h1 {
    margin-top: 2rem;
    font-size: 1.5rem;
    font-weight: 700;
    text-align: center;
    color: #951d1d;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  .form-area {
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    min-height: 100vh;
  }
`;
